/**
 * 多品牌资产切换器(Logo / QR / 标题预设公用)。
 *
 * 渲染:一个跟 AssetTile 同高度的卡(rounded-lg + bg-card/40),内部展示当前选中项 + ChevronDown。
 *      点开 popover,列所有 items + 设默认 + "去管理" 跳转。空 list 时显示 emptyHint + 跳管理。
 *
 * 用法:在 FreeCreatePage 的品牌资产卡里替换静态 AssetTile,共 3 个(logo/qr/标题预设)。
 */
import { useMemo, useState } from "react";
import { Ban, Check, ChevronDown, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

interface AssetItem {
  id: string;
  name: string;
}

interface Props<T extends AssetItem> {
  label: string;
  items: T[];
  selectedId: string | null;
  /**
   * V7 (2026-05-12) — onSelect 现在可能收到 null,语义 = "这次不用这个品牌资产"。
   * FreeCreate / ApplyTemplateSheet 都把 logoId/qrId/titleId state 改成可空,
   * 提交时传 undefined 给后端 brand 块对应字段。
   */
  onSelect: (id: string | null) => void;
  /** 当前选中项的渲染(放在 trigger 里);items[i] 的渲染需要带 logo / QR 缩略图。 */
  renderItem: (it: T) => React.ReactNode;
  /** popover 列表里每行的渲染 — 默认走 renderItem。 */
  renderRow?: (it: T) => React.ReactNode;
  emptyHint?: string;
  /**
   * 「最近用过」的 id(≤3,靠前展示)。
   * ★选品牌资产是**高重复**动作:同一个 logo 会被选几十次。20 个资产里每次翻到第 14 个,
   *   是这个控件最贵的一段时间。
   */
  recentIds?: string[];
  /** 跳转 /tenant-admin/brand-assets 之前调用 — 给外层 sheet/dialog 关闭自己的机会。 */
  onNavigateAway?: () => void;
}

export function AssetSwitcher<T extends AssetItem>({
  label,
  items,
  selectedId,
  onSelect,
  renderItem,
  renderRow,
  emptyHint,
  recentIds = [],
  onNavigateAway,
}: Props<T>) {
  // ★搜索框 **≥8 项才出现**:三个资产的租户不该看到一个空控件(空控件也是噪音)。
  const SEARCH_AT = 8;
  const [q, setQ] = useState("");
  const showSearch = items.length >= SEARCH_AT;
  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k ? items.filter((i) => i.name.toLowerCase().includes(k)) : items;
  }, [items, q]);
  const recent = useMemo(
    () =>
      q
        ? []
        : recentIds
            .map((id) => items.find((i) => i.id === id))
            .filter(Boolean)
            .slice(0, 3),
    [items, recentIds, q]
  ) as T[];
  const rest = useMemo(
    () => filtered.filter((i) => !recent.some((r) => r.id === i.id)),
    [filtered, recent]
  );
  const { t } = useTranslation("poster");
  const navigate = useNavigate();
  // V7 (2026-05-12) — selectedId === null 时显式表示用户选了"不使用",
  // trigger 不再 fallback 到 items[0],显式渲染"不使用本次"。
  const isSkipped = selectedId === null && items.length > 0;
  const selected = items.find((i) => i.id === selectedId) ?? null;
  const rowFn = renderRow ?? renderItem;

  function goManage() {
    onNavigateAway?.();
    navigate("/tenant-admin/brand-assets");
  }

  return (
    <div className="rounded-lg border border-border/60 bg-card/40 p-2.5">
      <div className="mb-1.5 text-2xs uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md p-1 text-left transition-colors hover:bg-card/60"
          >
            {isSkipped ? (
              /* V7.1 (2026-05-12) UI review — text-warning 跟 popover 选项里 active 状态的 warning 色对齐,
                 用户瞄一眼就知 "这是显式跳过" 而不是 "未配置"。 */
              <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-warning">
                <Ban className="h-3 w-3" />
                {t("assetSwitcher.skip")}
              </span>
            ) : selected ? (
              <div className="min-w-0 flex-1">{renderItem(selected)}</div>
            ) : (
              <span className="truncate text-[13px] text-muted-foreground">
                {emptyHint ?? t("assetSwitcher.unset")}
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-1" align="start">
          {items.length === 0 ? (
            <div className="space-y-2 p-3 text-center text-[13px]">
              <div className="text-muted-foreground">
                {emptyHint ?? t("assetSwitcher.noAssets")}
              </div>
              <button
                type="button"
                onClick={goManage}
                className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
              >
                <Plus className="h-3 w-3" />
                {t("assetSwitcher.goManage")}
              </button>
            </div>
          ) : (
            <>
              {/* 2026-07-30 —— 资产多起来之后这个弹层就没法用了:原来是**全量 map**、
                  容器 `w-72 p-1` 没有最大高度也没有滚动 —— 12 个 logo 撑到 ~560px,
                  30 个直接顶出视口,而且没有搜索。用户原话:「超过十个之后怎么选」。
                  ★不做分页:弹层里翻页要多两次点击、还丢上下文;分页是给表格的。
                    做法是「定高滚动 + 搜索 + 最近用过置顶 + 底栏常驻」。 */}
              {showSearch && (
                <div className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-2 py-1.5 m-1">
                  <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={`搜名称 · 共 ${items.length} 个`}
                    aria-label="搜索品牌资产"
                    className="w-full bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground/70"
                  />
                </div>
              )}
              {!showSearch && (
                <div className="px-2 py-1 text-2xs uppercase tracking-wider text-muted-foreground">
                  {t("assetSwitcher.optionsCount", { n: items.length })}
                </div>
              )}

              {/* ★列表定高滚动 —— 定高才有「还有更多」的可感知边界;
                  「本次不叠」与「去管理」留在滚动区**外面**,它们与列表长度无关。 */}
              <div className="max-h-[230px] overflow-y-auto">
                <button
                  type="button"
                  onClick={() => onSelect(null)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2 rounded p-2 text-left text-[13px] transition-colors",
                    isSkipped
                      ? "bg-warning/10 text-warning"
                      : "hover:bg-card/40 text-muted-foreground"
                  )}
                >
                  <Ban className="h-3 w-3 shrink-0" />
                  <span className="flex-1">{t("assetSwitcher.skip")}</span>
                  {isSkipped && <Check className="h-3 w-3 shrink-0" />}
                </button>

                {recent.length > 0 && (
                  <div className="px-2 pb-0.5 pt-1.5 text-2xs text-muted-foreground">
                    最近用过
                  </div>
                )}
                {[...recent, ...rest].map((it, i) => {
                  const sel = it.id === selectedId;
                  const firstRest = recent.length > 0 && i === recent.length;
                  return (
                    <div key={it.id}>
                      {firstRest && (
                        <div className="px-2 pb-0.5 pt-1.5 text-2xs text-muted-foreground">
                          全部
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => onSelect(it.id)}
                        className={cn(
                          "flex w-full cursor-pointer items-center gap-2 rounded p-2 text-left text-[13px] transition-colors",
                          sel ? "bg-primary/10 text-primary" : "hover:bg-card/40"
                        )}
                      >
                        <div className="min-w-0 flex-1">{rowFn(it)}</div>
                        {sel && <Check className="h-3 w-3 shrink-0" />}
                      </button>
                    </div>
                  );
                })}

                {filtered.length === 0 && (
                  <div className="p-3 text-center text-[12.5px] text-muted-foreground">
                    没有名字含「{q.trim()}」的资产
                  </div>
                )}
              </div>

              <div className="mt-1 border-t border-border/40 pt-1">
                <button
                  type="button"
                  onClick={goManage}
                  className="flex w-full cursor-pointer items-center gap-1.5 rounded p-2 text-left text-[13px] text-muted-foreground transition-colors hover:bg-card/40 hover:text-foreground"
                >
                  <Plus className="h-3 w-3" />
                  {t("assetSwitcher.goManage")}
                </button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
